import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, X, Mic, MicOff, Loader2, Globe, Check } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { useNutritionSearch } from "@/hooks/useNutritionSearch";
import { BRAZILIAN_FOODS, FOOD_CATEGORIES, LocalFood } from "@/data/brazilianFoods";

interface FoodSearchScreenProps {
  onSelect: (food: LocalFood) => void;
  onClose: () => void;
  initialQuery?: string;
}

const FoodSearchScreen: React.FC<FoodSearchScreenProps> = ({ onSelect, onClose, initialQuery = "" }) => {
  const { t, lang, tp } = useLanguage();
  const voice = useVoiceInput(lang);
  const { results: apiResults, searching: apiSearching, search: searchAPI, clear: clearAPI } = useNutritionSearch();

  const [query, setQuery] = useState(initialQuery);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [cart, setCart] = useState<LocalFood[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Whatever was in the box when the mic opened, so dictation extends the
  // query instead of replacing it.
  const voiceBaseline = useRef("");

  const withBaseline = (text: string) =>
    voiceBaseline.current ? `${voiceBaseline.current} ${text}` : text;

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, []);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  // Trigger API search on query change (debounced)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length >= 2) {
      debounceRef.current = setTimeout(() => searchAPI(query), 600);
    } else {
      clearAPI();
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, searchAPI, clearAPI]);

  // Local search results
  const localResults = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (q.length < 2) return [];
    return BRAZILIAN_FOODS.filter(
      (f) => f.name.toLowerCase().includes(q) || f.category.includes(q),
    );
  }, [query]);

  // Browsing mode (no query)
  const browseFoods = useMemo(() => {
    if (query.trim().length >= 2) return [];
    if (!selectedCategory) return BRAZILIAN_FOODS;
    return BRAZILIAN_FOODS.filter((f) => f.category === selectedCategory);
  }, [query, selectedCategory]);

  const isSearching = query.trim().length >= 2;
  const noLocalResults = isSearching && localResults.length === 0;

  const cartIds = useMemo(() => new Set(cart.map((f) => f.id)), [cart]);
  const totalKcal = useMemo(() => cart.reduce((s, f) => s + f.kcal, 0), [cart]);
  const totalProtein = useMemo(() => cart.reduce((s, f) => s + f.protein, 0), [cart]);
  const totalCarbs = useMemo(() => cart.reduce((s, f) => s + f.carbs, 0), [cart]);
  const totalFat = useMemo(() => cart.reduce((s, f) => s + f.fat, 0), [cart]);

  const toggleCart = (food: LocalFood) => {
    setCart((prev) =>
      prev.some((f) => f.id === food.id) ? prev.filter((f) => f.id !== food.id) : [...prev, food],
    );
  };

  const handleAddCart = () => {
    if (cart.length === 0) return;
    const combined: LocalFood = {
      id: `cart-${Date.now()}`,
      name: cart.length === 1 ? cart[0].name : cart.map((f) => f.name).join(" + "),
      category: cart[0].category,
      portion: cart.length === 1 ? cart[0].portion : cart.map((f) => f.portion).join(" + "),
      kcal: totalKcal,
      protein: totalProtein,
      carbs: totalCarbs,
      fat: totalFat,
      emoji: cart.length === 1 ? cart[0].emoji : "🍽️",
    };
    onSelect(combined);
  };

  // Mirror speech into the box as it is recognized — the debounced search then
  // runs on it like any typed query. startListening() takes no callback; this
  // component used to pass one, so dictation never reached the box (P0 28/07).
  useEffect(() => {
    if (!voice.transcript) return;
    setQuery(withBaseline(voice.transcript));
    setSelectedCategory(null);
  }, [voice.transcript]);

  const toggleVoice = () => {
    if (voice.isListening) {
      // The settled text; interim words already on screen may differ slightly.
      const finalText = voice.stopListening();
      if (finalText) setQuery(withBaseline(finalText));
      return;
    }
    voiceBaseline.current = query.trim();
    voice.startListening();
  };

  // Convert API result to LocalFood and toggle in cart
  const toggleApiResult = (r: typeof apiResults[number]) => {
    const food: LocalFood = {
      id: `api-${r.name}-${r.kcal}`,
      name: r.name,
      category: "external",
      portion: r.portion,
      kcal: r.kcal,
      protein: Math.round(r.protein),
      carbs: Math.round(r.carbs),
      fat: Math.round(r.fat),
      emoji: "🌐",
    };
    toggleCart(food);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex flex-col"
      style={{ zIndex: 200, background: "hsl(220 20% 5%)", WebkitOverflowScrolling: "touch" as any }}
    >
      {/* Ambient particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        {[
          { x: "15%", y: "20%", s: 4, d: "4s" },
          { x: "75%", y: "35%", s: 5, d: "6s" },
          { x: "40%", y: "60%", s: 3, d: "8s" },
          { x: "85%", y: "15%", s: 4, d: "5s" },
          { x: "25%", y: "75%", s: 6, d: "7s" },
        ].map((p, i) => (
          <div key={i} style={{
            position: "absolute", left: p.x, top: p.y, width: p.s, height: p.s,
            borderRadius: "50%", background: "hsl(187 72% 60% / 0.15)",
            animation: `floatUp ${p.d} ease-in-out infinite`,
          }} />
        ))}
      </div>

      <style>{`@keyframes floatUp { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-6px); } }`}</style>

      {/* HEADER — never scrolls */}
      <div className="flex-shrink-0 px-4 pt-4 pb-2 relative" style={{ zIndex: 1 }}>
        <div className="flex items-center gap-3 mb-3">
          <motion.button onClick={onClose} whileTap={{ scale: 0.9 }}
            className="w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}>
            <ChevronLeft size={18} className="text-white/60" />
          </motion.button>
          <p className="font-display text-[16px] font-semibold text-white">{t("nutrition.search_title")}</p>
        </div>

        {/* SEARCH INPUT */}
        <div className="relative">
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedCategory(null); }}
            placeholder={t("nutrition.search_placeholder")}
            className="w-full px-4 py-3 pr-20 rounded-xl text-[14px] font-body text-white placeholder:text-white/25 focus:outline-none transition-all"
            style={{
              background: "hsl(220 20% 9%)",
              border: "1px solid hsl(187 72% 50% / 0.25)",
            }}
          />
          <div className="absolute right-2 top-2 flex items-center gap-1">
            {query && (
              <motion.button onClick={() => { setQuery(""); clearAPI(); }} whileTap={{ scale: 0.85 }}
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: "rgba(255,255,255,0.04)" }}>
                <X size={14} className="text-white/40" />
              </motion.button>
            )}
            {voice.supported && (
              <motion.button onClick={toggleVoice} whileTap={{ scale: 0.85 }}
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{
                  background: voice.isListening ? "rgba(0,229,255,0.15)" : "rgba(0,229,255,0.06)",
                  border: `1px solid ${voice.isListening ? "rgba(0,229,255,0.4)" : "rgba(0,229,255,0.12)"}`,
                }}
                animate={voice.isListening ? { scale: [1, 1.1, 1] } : {}}
                transition={voice.isListening ? { duration: 1, repeat: Infinity } : {}}>
                {voice.isListening
                  ? <MicOff size={13} style={{ color: "#00e5ff" }} />
                  : <Mic size={13} style={{ color: "rgba(0,229,255,0.5)" }} />}
              </motion.button>
            )}
          </div>
        </div>

        {/* CATEGORY PILLS — only when browsing */}
        {!isSearching && (
          <div className="flex gap-2 mt-3 mb-1" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any, flexShrink: 0, paddingBottom: 4 }}>
            <motion.button
              onClick={() => setSelectedCategory(null)}
              whileTap={{ scale: 0.95 }}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-display font-medium"
              style={{
                background: !selectedCategory ? "hsl(187 72% 50% / 0.15)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${!selectedCategory ? "hsl(187 72% 50% / 0.50)" : "rgba(255,255,255,0.08)"}`,
                color: !selectedCategory ? "hsl(187 72% 70%)" : "rgba(255,255,255,0.45)",
                touchAction: "manipulation",
              }}
            >
              {t("nutrition.search_all")}
            </motion.button>
            {FOOD_CATEGORIES.map((cat) => (
              <motion.button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                whileTap={{ scale: 0.95 }}
                className="flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-display font-medium flex items-center gap-1.5"
                style={{
                  background: selectedCategory === cat.id ? "hsl(187 72% 50% / 0.15)" : "rgba(255,255,255,0.03)",
                  border: `1px solid ${selectedCategory === cat.id ? "hsl(187 72% 50% / 0.50)" : "rgba(255,255,255,0.08)"}`,
                  color: selectedCategory === cat.id ? "hsl(187 72% 70%)" : "rgba(255,255,255,0.45)",
                  touchAction: "manipulation",
                }}
              >
                <span className="text-sm leading-none">{cat.emoji}</span>
                {cat.label}
              </motion.button>
            ))}
          </div>
        )}
      </div>

      {/* FOOD LIST — scrolls */}
      <div className="flex-1 overflow-y-auto px-4 pb-6" style={{ WebkitOverflowScrolling: "touch" as any, overscrollBehavior: "contain", touchAction: "pan-y" }}>
        {/* BROWSING MODE */}
        {!isSearching && (
          <>
            {(selectedCategory
              ? FOOD_CATEGORIES.filter((c) => c.id === selectedCategory)
              : FOOD_CATEGORIES
            ).map((cat) => {
              const foods = browseFoods.filter((f) => f.category === cat.id);
              if (foods.length === 0) return null;
              return (
                <div key={cat.id} className="mb-4">
                  <p className="text-[10px] font-display tracking-[0.15em] uppercase mb-2 flex items-center gap-1.5"
                    style={{ color: "hsl(187 72% 50% / 0.60)" }}>
                    <span className="text-sm">{cat.emoji}</span> {cat.label}
                  </p>
                  <div className="space-y-1.5">
                    {foods.map((food, idx) => (
                      <FoodRow key={food.id} food={food} index={idx} onToggle={toggleCart} selected={cartIds.has(food.id)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </>
        )}

        {/* SEARCH MODE — local results */}
        {isSearching && localResults.length > 0 && (
          <div className="space-y-1.5 mb-4">
            {localResults.map((food, idx) => (
              <FoodRow key={food.id} food={food} index={idx} onToggle={toggleCart} selected={cartIds.has(food.id)} />
            ))}
          </div>
        )}

        {/* SEARCH MODE — no local results */}
        {noLocalResults && (
          <div className="text-center py-6">
            <p className="text-[12px] font-body" style={{ color: "rgba(255,255,255,0.35)" }}>
              {t("nutrition.search_not_found")}
            </p>
          </div>
        )}

        {/* API fallback results */}
        {isSearching && (apiSearching || apiResults.length > 0) && (
          <div className="mt-2">
            <p className="text-[10px] font-display tracking-[0.15em] uppercase mb-2 flex items-center gap-1.5"
              style={{ color: "rgba(255,255,255,0.30)" }}>
              <Globe size={11} /> {t("nutrition.search_external")}
            </p>
            {apiSearching && apiResults.length === 0 && (
              <div className="flex items-center gap-2 py-3 px-3">
                <Loader2 size={12} className="animate-spin" style={{ color: "hsl(187 72% 55%)" }} />
                <span className="text-[11px] font-body" style={{ color: "rgba(255,255,255,0.40)" }}>
                  {t("nutrition.search_searching")}
                </span>
              </div>
            )}
            <div className="space-y-1.5">
              {apiResults.map((r, idx) => {
                const apiId = `api-${r.name}-${r.kcal}`;
                const inCart = cartIds.has(apiId);
                return (
                  <motion.button
                    key={idx}
                    onClick={() => toggleApiResult(r)}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full text-left rounded-xl p-3 flex items-center gap-3"
                    style={{
                      background: inCart ? "hsl(187 72% 50% / 0.08)" : "hsl(220 20% 8% / 0.80)",
                      border: `1px solid ${inCart ? "hsl(187 72% 50% / 0.30)" : "hsl(220 20% 15%)"}`,
                    }}
                  >
                    <span className="text-2xl leading-none shrink-0">🌐</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-body text-white/80 truncate">{r.name}</p>
                      {r.brand && (
                        <p className="text-[10px] font-body" style={{ color: "rgba(255,255,255,0.30)" }}>{r.brand}</p>
                      )}
                      <div className="flex gap-1.5 mt-1">
                        <span className="text-[9px] font-display font-bold rounded-full px-1.5 py-0.5"
                          style={{ background: "rgba(168,85,246,0.12)", color: "#a855f6" }}>P{Math.round(r.protein)}g</span>
                        <span className="text-[9px] font-display font-bold rounded-full px-1.5 py-0.5"
                          style={{ background: "rgba(0,229,255,0.08)", color: "#00e5ff" }}>C{Math.round(r.carbs)}g</span>
                        <span className="text-[9px] font-display font-bold rounded-full px-1.5 py-0.5"
                          style={{ background: "rgba(234,179,8,0.10)", color: "#eab308" }}>G{Math.round(r.fat)}g</span>
                      </div>
                    </div>
                    {inCart ? (
                      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                        style={{ background: "hsl(187 72% 50% / 0.20)", border: "1px solid hsl(187 72% 50% / 0.50)" }}>
                        <Check size={14} style={{ color: "hsl(187 72% 60%)" }} />
                      </div>
                    ) : (
                      <div className="text-right shrink-0">
                        <p className="text-[14px] font-display font-semibold" style={{ color: "hsl(187 72% 55%)" }}>{r.kcal}</p>
                        <p className="text-[9px] font-display" style={{ color: "rgba(255,255,255,0.30)" }}>kcal</p>
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* CART SUMMARY BAR — flex child at bottom, not fixed */}
      {cart.length > 0 && (
        <div
          className="flex-shrink-0 flex items-center justify-between px-4 py-3"
          style={{
            background: "hsl(220 20% 6%)",
            backdropFilter: "blur(20px)",
            borderTop: "1px solid hsl(187 72% 50% / 0.35)",
            boxShadow: "0 -4px 20px hsl(187 72% 50% / 0.10)",
            paddingBottom: "max(12px, env(safe-area-inset-bottom))",
          }}
        >
          <div>
            <p className="text-[12px] font-body" style={{ color: "rgba(255,255,255,0.60)" }}>
              {cart.length} {tp("nutrition.cart_items", cart.length)}
            </p>
            <p className="text-[14px] font-display font-semibold" style={{ color: "hsl(187 72% 55%)" }}>
              {totalKcal} kcal
            </p>
          </div>
          <div className="flex items-center gap-2">
            <p className="text-[10px] font-body" style={{ color: "rgba(255,255,255,0.40)" }}>
              P{totalProtein}g · C{totalCarbs}g · G{totalFat}g
            </p>
            <button
              type="button"
              onClick={handleAddCart}
              className="rounded-xl px-5 py-2.5 font-display text-[13px] font-bold cursor-pointer"
              style={{
                zIndex: 201,
                position: "relative" as const,
                background: "linear-gradient(135deg, hsl(187 72% 45%), hsl(200 72% 50%))",
                color: "#0a0a0f",
                boxShadow: "0 4px 20px hsl(187 72% 45% / 0.40)",
                touchAction: "manipulation",
                WebkitTapHighlightColor: "transparent",
                userSelect: "none" as const,
              }}
            >
              {t("nutrition.cart_add")}
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// ── Food Row ─────────────────────────────────────────────────────────────

const FoodRow: React.FC<{
  food: LocalFood;
  index: number;
  onToggle: (food: LocalFood) => void;
  selected: boolean;
}> = ({ food, index, onToggle, selected }) => (
  <motion.button
    onClick={() => onToggle(food)}
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: Math.min(index * 0.03, 0.3) }}
    whileTap={{ scale: 0.98 }}
    className="w-full text-left rounded-xl px-4 py-4 flex items-center gap-3 cursor-pointer active:opacity-70"
    style={{
      background: selected ? "hsl(187 72% 50% / 0.08)" : "hsl(220 20% 8% / 0.80)",
      border: `1px solid ${selected ? "hsl(187 72% 50% / 0.30)" : "hsl(220 20% 15%)"}`,
      minHeight: 64,
      WebkitTapHighlightColor: "transparent",
      touchAction: "manipulation",
    }}
  >
    <span className="text-2xl leading-none shrink-0">{food.emoji}</span>
    <div className="flex-1 min-w-0">
      <p className="text-[13px] font-display font-medium text-white/85 truncate">{food.name}</p>
      <p className="text-[10px] font-body mt-0.5" style={{ color: "rgba(255,255,255,0.35)" }}>{food.portion}</p>
      <div className="flex gap-1.5 mt-1">
        <span className="text-[9px] font-display font-bold rounded-full px-1.5 py-0.5"
          style={{ background: "rgba(168,85,246,0.12)", color: "#a855f6" }}>P{food.protein}g</span>
        <span className="text-[9px] font-display font-bold rounded-full px-1.5 py-0.5"
          style={{ background: "rgba(0,229,255,0.08)", color: "#00e5ff" }}>C{food.carbs}g</span>
        <span className="text-[9px] font-display font-bold rounded-full px-1.5 py-0.5"
          style={{ background: "rgba(234,179,8,0.10)", color: "#eab308" }}>G{food.fat}g</span>
      </div>
    </div>
    {selected ? (
      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
        style={{ background: "hsl(187 72% 50% / 0.20)", border: "1px solid hsl(187 72% 50% / 0.50)" }}>
        <Check size={14} style={{ color: "hsl(187 72% 60%)" }} />
      </div>
    ) : (
      <div className="text-right shrink-0">
        <p className="text-[14px] font-display font-semibold" style={{ color: "hsl(187 72% 55%)" }}>{food.kcal}</p>
        <p className="text-[9px] font-display" style={{ color: "rgba(255,255,255,0.30)" }}>kcal</p>
      </div>
    )}
  </motion.button>
);

export default FoodSearchScreen;
